import { useState, useEffect, useCallback } from 'react';
import { getSettings, updateSettings } from '../api/client';

interface GlobalSettings {
  moderationEnabled: boolean;
  aiApprovedPinsRequireModeration: boolean;
  submissionLimitsEnabled: boolean;
  dailySubmissionLimit: number;
  statsTrackingEnabled: boolean;
  leaderboardsEnabled: boolean;
  achievementsEnabled: boolean;
}

export default function Settings() {
  const [settings, setSettings] = useState<GlobalSettings>({
    moderationEnabled: true,
    aiApprovedPinsRequireModeration: false,
    submissionLimitsEnabled: true,
    dailySubmissionLimit: 10,
    statsTrackingEnabled: true,
    leaderboardsEnabled: false,
    achievementsEnabled: false,
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [hasChanges, setHasChanges] = useState(false);
  const [originalSettings, setOriginalSettings] = useState<GlobalSettings | null>(null);

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await getSettings();
      const data = result.data;
      if (data) {
        const loadedSettings = {
          moderationEnabled: data.moderationEnabled ?? true,
          aiApprovedPinsRequireModeration: data.aiApprovedPinsRequireModeration ?? false,
          submissionLimitsEnabled: data.submissionLimitsEnabled ?? true,
          dailySubmissionLimit: data.dailySubmissionLimit ?? 10,
          statsTrackingEnabled: data.statsTrackingEnabled ?? true,
          leaderboardsEnabled: data.leaderboardsEnabled ?? false,
          achievementsEnabled: data.achievementsEnabled ?? false,
        };
        setSettings(loadedSettings);
        setOriginalSettings(loadedSettings);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  useEffect(() => {
    if (originalSettings) {
      setHasChanges(JSON.stringify(settings) !== JSON.stringify(originalSettings));
    }
  }, [settings, originalSettings]);

  const handleToggle = (key: keyof GlobalSettings) => {
    setSettings(prev => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await updateSettings(settings as unknown as Record<string, unknown>);
      setOriginalSettings(settings);
      setHasChanges(false);
      alert('Settings saved successfully!');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12 text-gray-500">
        Loading settings...
      </div>
    );
  }

  return (
    <div>
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start gap-4 mb-6 sm:mb-8 pb-5 border-b-2 border-gray-200">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-800 mb-1">Global Settings</h2>
          <div className="text-gray-500 text-sm sm:text-base">Configure system-wide settings</div>
        </div>
        <div className="flex gap-2 w-full sm:w-auto">
          <button
            onClick={fetchSettings}
            disabled={loading}
            className="flex-1 sm:flex-none px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors text-sm disabled:opacity-50"
          >
            Reload
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !hasChanges}
            className="flex-1 sm:flex-none px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors text-sm disabled:opacity-50"
          >
            {saving ? 'Saving...' : hasChanges ? 'Save Changes' : 'Saved'}
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 text-red-500 p-4 rounded-lg mb-6">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Moderation Settings */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="bg-slate-700 text-white px-4 sm:px-5 py-3 sm:py-4">
            <span className="font-bold text-sm sm:text-base">&#x2705; Moderation</span>
          </div>
          <div className="p-4 sm:p-5 space-y-4">
            <div className="flex items-center justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="font-medium text-slate-800 text-sm sm:text-base">Hold AI-Approved Pins for Review</div>
                <div className="text-xs sm:text-sm text-gray-500">
                  When ON, AI-approved pins are held server-side until you manually approve them in the AI Approved queue. When OFF, AI-approved pins ship to client devices alongside human-approved pins.
                </div>
              </div>
              <button
                onClick={() => handleToggle('aiApprovedPinsRequireModeration')}
                className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ${settings.aiApprovedPinsRequireModeration ? 'bg-blue-500' : 'bg-gray-300'}`}
              >
                <div className={`absolute w-5 h-5 bg-white rounded-full top-0.5 transition-transform ${settings.aiApprovedPinsRequireModeration ? 'translate-x-5' : 'translate-x-0.5'}`} />
              </button>
            </div>
          </div>
        </div>

        {/* Submission Limits */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="bg-slate-700 text-white px-4 sm:px-5 py-3 sm:py-4">
            <span className="font-bold text-sm sm:text-base">&#x1F4E5; Submissions</span>
          </div>
          <div className="p-4 sm:p-5 space-y-4">
            <div className="flex items-center justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="font-medium text-slate-800 text-sm sm:text-base">Submission Limits</div>
                <div className="text-xs sm:text-sm text-gray-500">Limit daily submissions per user</div>
              </div>
              <button
                onClick={() => handleToggle('submissionLimitsEnabled')}
                className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ${settings.submissionLimitsEnabled ? 'bg-blue-500' : 'bg-gray-300'}`}
              >
                <div className={`absolute w-5 h-5 bg-white rounded-full top-0.5 transition-transform ${settings.submissionLimitsEnabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
              </button>
            </div>

            {settings.submissionLimitsEnabled && (
              <div>
                <label className="block text-sm font-medium text-slate-800 mb-2">
                  Daily Limit
                </label>
                <input
                  type="number"
                  value={settings.dailySubmissionLimit}
                  onChange={(e) => setSettings(prev => ({ ...prev, dailySubmissionLimit: parseInt(e.target.value) || 0 }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            )}
          </div>
        </div>

        {/* Analytics Settings */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="bg-slate-700 text-white px-4 sm:px-5 py-3 sm:py-4">
            <span className="font-bold text-sm sm:text-base">&#x1F4CA; Analytics</span>
          </div>
          <div className="p-4 sm:p-5 space-y-4">
            <div className="flex items-center justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="font-medium text-slate-800 text-sm sm:text-base">Stats Tracking</div>
                <div className="text-xs sm:text-sm text-gray-500">Track user activity and events</div>
              </div>
              <button
                onClick={() => handleToggle('statsTrackingEnabled')}
                disabled={true}
                className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ${settings.statsTrackingEnabled ? 'bg-blue-500' : 'bg-gray-300'} cursor-not-allowed opacity-75`}
                title="Always enabled"
              >
                <div className={`absolute w-5 h-5 bg-white rounded-full top-0.5 transition-transform ${settings.statsTrackingEnabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
              </button>
            </div>
          </div>
        </div>

        {/* Gamification Settings */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="bg-slate-700 text-white px-4 sm:px-5 py-3 sm:py-4">
            <span className="font-bold text-sm sm:text-base">&#x1F3AE; Gamification</span>
          </div>
          <div className="p-4 sm:p-5 space-y-4">
            <div className="flex items-center justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="font-medium text-slate-800 text-sm sm:text-base">Leaderboards</div>
                <div className="text-xs sm:text-sm text-gray-500">Enable competitive leaderboards (coming soon)</div>
              </div>
              <button
                onClick={() => handleToggle('leaderboardsEnabled')}
                disabled={true}
                className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ${settings.leaderboardsEnabled ? 'bg-blue-500' : 'bg-gray-300'} cursor-not-allowed opacity-50`}
                title="Coming soon"
              >
                <div className={`absolute w-5 h-5 bg-white rounded-full top-0.5 transition-transform ${settings.leaderboardsEnabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
              </button>
            </div>

            <div className="flex items-center justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="font-medium text-slate-800 text-sm sm:text-base">Achievements</div>
                <div className="text-xs sm:text-sm text-gray-500">Enable achievement badges (coming soon)</div>
              </div>
              <button
                onClick={() => handleToggle('achievementsEnabled')}
                disabled={true}
                className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ${settings.achievementsEnabled ? 'bg-blue-500' : 'bg-gray-300'} cursor-not-allowed opacity-50`}
                title="Coming soon"
              >
                <div className={`absolute w-5 h-5 bg-white rounded-full top-0.5 transition-transform ${settings.achievementsEnabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Danger Zone */}
      <div className="mt-6 bg-white rounded-lg shadow overflow-hidden border-2 border-red-200">
        <div className="bg-red-600 text-white px-4 sm:px-5 py-3 sm:py-4">
          <span className="font-bold text-sm sm:text-base">&#x26A0;&#xFE0F; Danger Zone</span>
        </div>
        <div className="p-4 sm:p-5 space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <div className="font-medium text-slate-800 text-sm sm:text-base">Clear All Cache</div>
              <div className="text-xs sm:text-sm text-gray-500">Clear KV cache and force refresh</div>
            </div>
            <button
              disabled
              className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-red-600 border border-red-300 rounded hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Not implemented"
            >
              Clear Cache
            </button>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <div className="font-medium text-slate-800 text-sm sm:text-base">Reset Statistics</div>
              <div className="text-xs sm:text-sm text-gray-500">Clear all user statistics (cannot be undone)</div>
            </div>
            <button
              disabled
              className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-red-600 border border-red-300 rounded hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Not implemented"
            >
              Reset Stats
            </button>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <div className="font-medium text-slate-800 text-sm sm:text-base">Purge Availability Events</div>
              <div className="text-xs sm:text-sm text-gray-500">Delete events older than 30 days</div>
            </div>
            <button
              disabled
              className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-red-600 border border-red-300 rounded hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Not implemented"
            >
              Purge Events
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
